import moment from "moment";

import { StandardDataTimeRangeDimension } from "@/lib/api";

import { Dimension, DimensionValue } from ".";

export class TimeRangeDimensionValue implements DimensionValue {
  public timestamp: Date;

  constructor(timestamp: Date) {
    this.timestamp = timestamp;
  }

  get queryStringValue() {
    return this.timestamp.toISOString().slice(0, -5) + "Z";
  }
}

export type TimeRangeDimensionProps = {
  id: string;
  values: TimeRangeDimensionValue[];
  start: TimeRangeDimensionValue;
  end: TimeRangeDimensionValue;
  intervalInMilliseconds: number;
};

export class TimeRangeDimension implements Dimension {
  private props;

  constructor(props: TimeRangeDimensionProps) {
    this.props = props;
  }

  static fromData(data: StandardDataTimeRangeDimension) {
    if (data.values.length === 0)
      throw new Error("TimeRange Dimension without values found");

    return new TimeRangeDimension({
      id: data.id,
      values: data.values.map((value) => new TimeRangeDimensionValue(new Date(value))),
      start: new TimeRangeDimensionValue(new Date(data.start)),
      end: new TimeRangeDimensionValue(new Date(data.end)),
      intervalInMilliseconds: moment.duration(data.interval).asMilliseconds(),
    });
  }

  get id() {
    return this.props.id;
  }
  get values() {
    return this.props.values;
  }
  get start() {
    return this.props.start;
  }
  get end() {
    return this.props.end;
  }
  get intervalInMilliseconds() {
    return this.props.intervalInMilliseconds;
  }

  get default() {
    return this.props.start;
  }

  next(current: DimensionValue) {
    if (!(current instanceof TimeRangeDimensionValue)) return undefined;

    const index = this.props.values.findIndex(
      (value) => value.queryStringValue === current.queryStringValue,
    );
    return this.props.values[index + 1];
  }

  contains(value: DimensionValue) {
    if (!(value instanceof TimeRangeDimensionValue)) return false;
    return value.timestamp >= this.start.timestamp && value.timestamp <= this.end.timestamp;
  }

  snap(value: DimensionValue) {
    if (!(value instanceof TimeRangeDimensionValue)) return undefined;

    if (value.timestamp < this.start.timestamp) return this.start;
    if (value.timestamp > this.end.timestamp) return this.end;

    return this.values.findLast((it) => it.timestamp <= value.timestamp);
  }

  merge(other: Dimension) {
    if (!(other instanceof TimeRangeDimension))
      throw new Error("attempted to merge dimensions of different types");
    if (other.id !== this.id)
      throw new Error("attempted to merge dimensions with different IDs");

    const start = new TimeRangeDimensionValue(
      new Date(Math.min(+this.start.timestamp, +other.start.timestamp)),
    );
    const end = new TimeRangeDimensionValue(
      new Date(Math.max(+this.end.timestamp, +other.end.timestamp)),
    );
    const intervalInMilliseconds = Math.min(
      this.intervalInMilliseconds,
      other.intervalInMilliseconds,
    );

    const values: TimeRangeDimensionValue[] = [];
    let current = start;
    while (current.timestamp <= end.timestamp) {
      values.push(current);
      current = new TimeRangeDimensionValue(
        new Date(+current.timestamp + intervalInMilliseconds),
      );
    }

    return new TimeRangeDimension({
      ...this.props,
      start,
      end,
      intervalInMilliseconds,
      values,
    });
  }

  slice(start: Date, end: Date) {
    const values = this.values.filter(
      (value) => value.timestamp >= start && value.timestamp <= end,
    );

    if (!values.length) return undefined;

    return new TimeRangeDimension({
      ...this.props,
      start: values[0],
      end: values[values.length - 1],
      values,
    });
  }
}
